import { createRequisition, createRequisitionComment, submitRequisition, updateRequisition } from "../api/requisitions";
import type { OfflineQueueRecord } from "../types/requisition";
import { getDraft, saveDraft } from "./indexedDB/draftStore";
import { listQueuedIntents, updateQueuedIntent } from "./indexedDB/queueStore";
import { recordLastSyncedAt } from "./indexedDB/syncMetadata";

interface ReplayResult {
  processedCount: number;
  failedIntentId: string | null;
}

function getErrorMessage(error: unknown): string {
  if (error instanceof Error && error.message) {
    return error.message;
  }

  return "Unknown replay error";
}

async function markIntent(
  intent: OfflineQueueRecord,
  status: OfflineQueueRecord["status"],
  options: { lastError?: string | null; serverRequisitionId?: string | null } = {},
): Promise<OfflineQueueRecord> {
  const nextIntent: OfflineQueueRecord = {
    ...intent,
    status,
    serverRequisitionId: options.serverRequisitionId ?? intent.serverRequisitionId,
    lastError: options.lastError ?? null,
    updatedAt: new Date().toISOString(),
  };

  await updateQueuedIntent(nextIntent);
  return nextIntent;
}

async function resolveServerRequisitionId(
  intent: OfflineQueueRecord,
  resolvedServerIds: Map<string, string>,
): Promise<string | null> {
  if (intent.serverRequisitionId) {
    return intent.serverRequisitionId;
  }

  const resolved = resolvedServerIds.get(intent.localDraftId);
  if (resolved) {
    return resolved;
  }

  const draft = await getDraft(intent.localDraftId);
  return draft?.serverRequisitionId ?? null;
}

export async function replayPendingRequisitionIntents(): Promise<ReplayResult> {
  const pendingIntents = (await listQueuedIntents()).filter((intent) =>
    intent.status === "pending" || intent.status === "failed" || intent.status === "replaying"
  );
  const resolvedServerIds = new Map<string, string>();
  const resolvedCommentIds = new Map<string, string>();
  let processedCount = 0;

  for (const intent of pendingIntents) {
    const replayingIntent = await markIntent(intent, "replaying");

    try {
      if (replayingIntent.intentType === "create_draft") {
        const created = await createRequisition(replayingIntent.payload);
        const serverRequisitionId = created.data.id;
        resolvedServerIds.set(replayingIntent.localDraftId, serverRequisitionId);

        const draft = await getDraft(replayingIntent.localDraftId);
        if (draft) {
          await saveDraft({
            ...draft,
            serverRequisitionId,
            updatedAt: new Date().toISOString(),
          });
        }

        await markIntent(replayingIntent, "completed", { serverRequisitionId });
      }

      if (replayingIntent.intentType === "update_draft") {
        const serverRequisitionId = await resolveServerRequisitionId(replayingIntent, resolvedServerIds);
        if (!serverRequisitionId) {
          throw new Error("Missing server requisition mapping");
        }

        await updateRequisition(serverRequisitionId, replayingIntent.payload);
        await markIntent(replayingIntent, "completed", { serverRequisitionId });
      }

      if (replayingIntent.intentType === "submit_draft") {
        const serverRequisitionId = await resolveServerRequisitionId(replayingIntent, resolvedServerIds);
        if (!serverRequisitionId) {
          throw new Error("Missing server requisition mapping");
        }

        await submitRequisition(serverRequisitionId);
        await markIntent(replayingIntent, "completed", { serverRequisitionId });
      }

      if (replayingIntent.intentType === "create_comment") {
        const serverRequisitionId = await resolveServerRequisitionId(replayingIntent, resolvedServerIds);
        if (!serverRequisitionId) {
          throw new Error("Missing server requisition mapping");
        }

        const resolvedParentId =
          replayingIntent.payload.parent_id && resolvedCommentIds.has(replayingIntent.payload.parent_id)
            ? resolvedCommentIds.get(replayingIntent.payload.parent_id) ?? null
            : replayingIntent.payload.parent_id ?? null;

        const created = await createRequisitionComment(serverRequisitionId, {
          text: replayingIntent.payload.text,
          parent_id: resolvedParentId,
          client_comment_id: replayingIntent.payload.client_comment_id,
        });
        resolvedCommentIds.set(replayingIntent.payload.client_comment_id, created.data.id);
        await markIntent(replayingIntent, "completed", { serverRequisitionId });
      }

      processedCount += 1;
    } catch (error) {
      await markIntent(replayingIntent, "failed", {
        lastError: getErrorMessage(error),
      });
      return {
        processedCount,
        failedIntentId: replayingIntent.id,
      };
    }
  }

  if (processedCount > 0) {
    await recordLastSyncedAt(new Date().toISOString());
  }

  return {
    processedCount,
    failedIntentId: null,
  };
}
